import moment from "moment";
import * as R from "ramda";

import { createSelector } from "reselect";
import { getters } from "Portal/PortalHome/model";
import { getters as SearchBarGetters } from "ui-kit/SearchBar";
import { TASKS_ORDER, CART_REQUESTS_ID } from "Portal/PortalHome/constants";
import { ACTION_TYPES } from "ui-kit/Table/constants";

import { ROW_ACTIONS_ITEMS, FILTER } from "Portal/PortalHome/constants";
import { getDate7DaysAgo } from "./utils";
import { getFontCssName, resolveFontCss } from "utils/resolve-font-css";

export const getRowActions = R.always([
  {
    id: ROW_ACTIONS_ITEMS.OPEN_SUBMISSION_MODAL,
    type: ACTION_TYPES.ITEM,
    name: "Open Submission Modal",
    leftIcon: "open_in_new"
  },
  {
    id: ROW_ACTIONS_ITEMS.EDIT_ROW,
    type: ACTION_TYPES.ITEM,
    name: "Edit",
    leftIcon: "edit"
  },
  {
    id: ROW_ACTIONS_ITEMS.DELETE_ROW,
    type: ACTION_TYPES.ITEM,
    name: "Delete",
    leftIcon: "delete"
  }
]);

import { eventDetails as getEventDetails } from "redux/modules/portal/selectors";

export const getMySessionsTitle = createSelector(
  getEventDetails,
  eventDetails => {
    // handle: SABCS custom header
    return eventDetails.org_id === "be6268dd-ac99-4d19-94a4-af1ca2acd763"
      ? "My Abstracts"
      : "My Sessions";
  }
);

export const tasksSelector = createSelector(getters.tasks, tasks =>
  TASKS_ORDER.map(({ text, id }) => ({
    title: text,
    id: id,
    data: tasks[id] || []
  }))
);

export const filteredTasks = createSelector(
  tasksSelector,
  SearchBarGetters.searchTerm,
  getters.dueDate,
  getters.filters,
  (tasks, searchTerm, dueDate, filters) => {
    const activeFilter = R.find(f => f.selected, filters);
    const filteredTasks = R.compose(
      R.map(task => ({
        ...task,
        data: R.reject(subtask => {
          if (activeFilter || (activeFilter && activeFilter.id === "all")) {
            const { id } = activeFilter;

            const isPending =
              (subtask.status === "not_submitted" ||
                subtask.status === "incomplete") &&
              id === "done";

            const isDone =
              (subtask.status === "complete" || subtask.status === "denied") &&
              id === "pending";

            return isPending || isDone;
          }
          return false;
        }, task.data)
      })),
      R.map(task => ({
        ...task,
        data: R.filter(
          subtask =>
            R.toLower(subtask.name ? subtask.name : "").includes(
              R.toLower(searchTerm)
            ),
          task.data
        )
      }))
    )(tasks);

    if (!dueDate) {
      return [
        {
          id: "unique",
          data: filteredTasks.reduce((acc, curr) => acc.concat(curr.data), [])
        }
      ];
    }
    return filteredTasks;
  }
);

export const getSubmissionsTableData = createSelector(
  getters.tasks,
  R.compose(R.find(R.propEq("id", CART_REQUESTS_ID)), payload => [
    ...R.propOr([], "past_due", payload),
    ...R.propOr([], "due_next_7_days", payload),
    ...R.propOr([], "due_this_month", payload)
  ])
);

export const getDueNext7Days = createSelector(
  getters.tasks,
  R.compose(
    R.length,
    R.filter(is_complete => !is_complete),
    R.map(R.prop("is_complete")),
    payload => [...R.propOr([], "due_next_7_days", payload)]
  )
);

export const getPastDue = createSelector(
  getters.tasks,
  R.compose(
    R.length,
    R.filter(date => getDate7DaysAgo(date.split("T")[0]) <= 7),
    R.map(R.prop("due_raw")),
    payload => [...R.propOr([], "past_due", payload)]
  )
);

export const getDueToday = createSelector(
  getters.tasks,
  R.compose(
    R.length,
    R.filter(
      date =>
        moment(date).format("YYYY-MM-DD") ===
        moment(new Date())
          .utc()
          .format("YYYY-MM-DD")
    ),
    R.map(R.prop("due_raw")),
    payload => [
      ...R.propOr(
        [],
        "due_next_7_days",
        R.filter(task => !task.is_complete, payload)
      )
    ]
  )
);

export const getSubmissionsInstanceIds = createSelector(
  getters.tasks,
  R.compose(R.map(R.prop("id")), payload => [
    ...R.propOr([], "past_due", payload),
    ...R.propOr([], "due_next_7_days", payload),
    ...R.propOr([], "due_this_month", payload)
  ])
);

export const getCountOfAllCompletedTasks = createSelector(
  getters.tasks,
  R.compose(
    R.length,
    R.filter(is_complete => is_complete),
    R.map(R.prop("is_complete")),
    payload => [
      ...R.propOr([], "due_next_7_days", payload),
      ...R.propOr([], "past_due", payload),
      ...R.propOr(
        [],
        "due_this_month",
        R.filter(task => task.is_complete, payload)
      )
    ]
  )
);

export const getCountOfAllTasks = createSelector(
  getters.tasks,
  R.compose(R.length, R.map(R.prop("is_complete")), payload => [
    ...R.propOr([], "due_next_7_days", payload),
    ...R.propOr([], "past_due", payload),
    ...R.propOr(
      [],
      "due_this_month",
      R.filter(task => task.is_complete, payload)
    )
  ])
);

export const getCountOfAllSections = createSelector(
  getters.tasks,
  R.compose(R.length, R.map(R.prop("id")), payload => [
    ...R.propOr([], "past_due", payload),
    ...R.propOr([], "due_next_7_days", payload),
    ...R.propOr([], "due_this_month", payload)
  ])
);

export const filteredTabs = createSelector(
  getters.tabs,
  getters.count_of_schedule_items,
  (tabs, countOfScheduleItems) => {
    if (!countOfScheduleItems) {
      return R.filter(t => t.id !== FILTER.SCHEDULE)(tabs);
    }

    return tabs;
  }
);

export const getFiles = createSelector(getters.files, files => files);

export const getSessions = createSelector(
  getters.sessions,
  sessions => sessions
);

export const getApplications = createSelector(
  getters.applications,
  applications => applications
);

export const getPages = createSelector(getters.pages, pages => pages);

export const getAlerts = createSelector(getters.alerts, alerts => alerts);

export const getLinks = createSelector(getters.gleanin_links, links => links);

/**
 * Styles
 */
export const getLogoImageUrl = createSelector(
  getters.styles,
  R.prop("logo_image_url")
);
export const getBackgroundImageUrl = createSelector(
  getters.styles,
  R.prop("background_image_url")
);
export const getLogoImageAltText = createSelector(
  getters.styles,
  R.prop("logo_alt_text")
);
export const getBackgroundImageAltText = createSelector(
  getters.styles,
  R.prop("background_image_url_alt_text")
);
export const getButtonColor = createSelector(
  getters.styles,
  R.prop("button_color")
);
export const getButtonTextColor = createSelector(
  getters.styles,
  R.prop("button_text_color")
);
export const getBackgroundColor = createSelector(
  getters.styles,
  R.prop("background_color")
);
export const getPortalNameFont = createSelector(
  getters.styles,
  R.compose(getFontCssName, R.prop("portal_name_font"))
);
export const getPortalNameSize = createSelector(
  getters.styles,
  R.compose(size => (size ? `${size}px` : null), R.prop("portal_name_size"))
);
export const getPortalNameColor = createSelector(
  getters.styles,
  R.prop("portal_name_color")
);
export const getSectionHeaderFont = createSelector(
  getters.styles,
  R.compose(getFontCssName, R.prop("section_header_font"))
);
export const getSectionHeaderSize = createSelector(
  getters.styles,
  R.compose(size => (size ? `${size}px` : null), R.prop("section_header_size"))
);
export const getSectionHeaderColor = createSelector(
  getters.styles,
  R.prop("section_header_color")
);

export const getCssCode = createSelector(getters.styles, styles => {
  let css = "";

  const fonts = R.compose(
    R.uniq,
    R.filter(f => f)
  )([styles.portal_name_font, styles.section_header_font]);

  const fontCss = resolveFontCss({ fonts });
  if (fontCss && fontCss.length) {
    css = `${fontCss}`;
  }

  if (styles.css_code && styles.css_code.length) {
    css = `${css} 

${styles.css_code}`;
  }

  return css;
});
